import clsx from 'clsx';
import { Icon } from '../../../layout/Icon.jsx';
import { Pill } from '../../../layout/Pill.jsx';
import { PhotoCapture } from '../components/PhotoCapture.jsx';
import { ChildSelectModal } from '../components/ChildSelectModal.jsx';
import { useReceptionForm } from '../hooks/useReceptionForm.js';

export { GENDER_LABEL };

// Backend enum: male | female. Display Arabic labels, transmit English keys.
const GENDERS = [
  { key: 'male', label: 'ذكر' },
  { key: 'female', label: 'أنثى' }
];

const GENDER_LABEL = { male: 'ذكر', female: 'أنثى' };

function ChildBlock({ child, index, total }) {
  const updateChild = useReceptionForm((s) => s.updateChild);
  const removeChild = useReceptionForm((s) => s.removeChild);
  const attemptedNext = useReceptionForm((s) => s.attemptedNext);

  // نفس منطق isStep2Valid لكن لكل حقل على حدة، حتى نُلوّن الحقل الناقص فقط.
  const nameInvalid = (child.name || '').trim().length < 2;
  const ageStr = String(child.age ?? '').trim();
  const ageNum = Number(ageStr);
  const ageInvalid = ageStr === '' || !Number.isInteger(ageNum) || ageNum < 0 || ageNum > 18;
  const showErrors = attemptedNext;

  return (
    <div className="border border-line rounded-[14px] p-3.5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <h4 className="font-bold text-[13.5px]">الطفل #{index + 1}</h4>
          {child.childId ? <Pill variant="orange">مسجّل سابقاً</Pill> : null}
        </div>
        {total > 1 ? (
          <button
            type="button"
            className="btn btn-ghost text-red"
            onClick={() => removeChild(child.tempId)}
            style={{ padding: '4px 8px', fontSize: 12 }}
          >
            <Icon name="trash" size={14} /> إزالة
          </button>
        ) : null}
      </div>

      <div className="grid gap-4" style={{ gridTemplateColumns: '180px 1fr' }}>
        <PhotoCapture
          photoUrl={child.photoUrl}
          onCapture={(blob, url) => updateChild(child.tempId, { photoBlob: blob, photoUrl: url })}
          onClear={() => updateChild(child.tempId, { photoBlob: null, photoUrl: null })}
        />

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-ink-2 mb-1">اسم الطفل *</label>
              <input
                className={clsx('input', showErrors && nameInvalid && 'border-red border-2')}
                value={child.name}
                onChange={(e) => updateChild(child.tempId, { name: e.target.value })}
                placeholder="مثال: تقى العامري"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-ink-2 mb-1">العمر *</label>
              <input
                className={clsx('input', showErrors && ageInvalid && 'border-red border-2')}
                type="number"
                min={0}
                max={18}
                step={1}
                value={child.age}
                onChange={(e) => updateChild(child.tempId, { age: e.target.value })}
                placeholder="بالسنوات"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-ink-2 mb-1">الجنس</label>
              <div className="flex gap-1.5">
                {GENDERS.map((g) => (
                  <button
                    key={g.key}
                    type="button"
                    className={`btn ${child.gender === g.key ? 'btn-primary' : ''}`}
                    style={{ padding: '6px 14px', fontSize: 12 }}
                    onClick={() => updateChild(child.tempId, { gender: g.key })}
                  >
                    {g.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-ink-2 mb-1">الطول (سم)</label>
              <input
                className="input"
                type="number"
                value={child.height}
                onChange={(e) => updateChild(child.tempId, { height: e.target.value })}
                placeholder="اختياري"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-ink-2 mb-1">الوزن (كغ)</label>
              <input
                className="input"
                type="number"
                value={child.weight}
                onChange={(e) => updateChild(child.tempId, { weight: e.target.value })}
                placeholder="اختياري"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-ink-2 mb-1">ملاحظات صحية</label>
              <textarea
                className="input"
                rows={2}
                value={child.medicalNotes}
                onChange={(e) => updateChild(child.tempId, { medicalNotes: e.target.value })}
                placeholder="ربو، صرع..."
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-ink-2 mb-1">حساسية</label>
              <textarea
                className="input"
                rows={2}
                value={child.allergyNotes}
                onChange={(e) => updateChild(child.tempId, { allergyNotes: e.target.value })}
                placeholder="حساسية فول سوداني..."
              />
            </div>
          </div>

          <div className="border border-line rounded-[10px] p-3 bg-paper-2/50">
            <label className="flex items-center gap-2 text-xs font-semibold text-ink-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={!!child.useCard}
                onChange={(e) =>
                  updateChild(child.tempId, {
                    useCard: e.target.checked,
                    cardUid: e.target.checked ? child.cardUid : ''
                  })
                }
              />
              ربط كارت RFID للطفل (اختياري)
            </label>
            {child.useCard ? (
              <input
                className="input mt-2"
                placeholder="امرر الكارت أو أدخل الرمز يدوياً"
                value={child.cardUid || ''}
                autoFocus
                onChange={(e) =>
                  updateChild(child.tempId, { cardUid: e.target.value.toUpperCase() })
                }
              />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export function Step2Child() {
  const children = useReceptionForm((s) => s.children);
  const addChild = useReceptionForm((s) => s.addChild);
  const existingChildren = useReceptionForm((s) => s.existingChildren);
  const setChildPickerOpen = useReceptionForm((s) => s.setChildPickerOpen);

  // أطفال مسجّلون سابقاً لم يُدخلوا بعد — يظهر زر استيرادهم من هنا أيضاً.
  const importedIds = new Set(children.filter((c) => c.childId).map((c) => String(c.childId)));
  const remainingExisting = existingChildren.filter((c) => !importedIds.has(String(c._id || c.id)));

  return (
    <div>
      <div className="card-h">
        <div className="flex-1">
          <h3 className="text-[14.5px] font-bold">بيانات الأطفال</h3>
          <div className="text-xs text-ink-3 mt-0.5">يمكن إضافة أكثر من طفل لنفس ولي الأمر</div>
        </div>
        {remainingExisting.length > 0 ? (
          <button
            type="button"
            className="btn"
            onClick={() => setChildPickerOpen(true)}
            style={{ fontSize: 12 }}
          >
            <Icon name="reception" size={14} /> أطفال سابقون ({remainingExisting.length})
          </button>
        ) : null}
        <button
          type="button"
          className="btn"
          onClick={addChild}
          style={{ fontSize: 12 }}
        >
          <Icon name="plus" size={14} /> إضافة طفل
        </button>
      </div>
      <div className="card-b space-y-3">
        {children.map((c, i) => (
          <ChildBlock key={c.tempId} child={c} index={i} total={children.length} />
        ))}
      </div>

      <ChildSelectModal />
    </div>
  );
}
